import React, { useContext, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import PropTypes from 'prop-types';
import { z } from 'zod';
import { required } from '../../../../../../utils/validation/zodValidators';
import { Modes } from './constants';
import { ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { EditBankAccountDrawerContext } from './EditBankAccountDrawerContext';
import { BankAccountDrawer } from './BankAccountDrawer';
import { useBankAccountsStore } from '../store';
import { BankAccountModel } from '../constants';

const Base = z.object({
    description: z
        .string()
        .trim()
        .nullable(),
    bankAccountName: required(),
    bankAccountModel: required()
});

const IbanAccount = Base.extend({
    bankAccountModel: z.literal('IBAN'),
    iban: required(),
    bic: required()
});
const ClabeAccount = Base.extend({
    bankAccountModel: z.literal('CLABE'),
    clabe: required(),
    bankName: required()
});
const RbcAccount = Base.extend({
    bankAccountModel: z.literal('RBC'),
    transitNumber: required(),
    institutionNumber: required(),
    accountNumber: required()
});

const FormModel = z.discriminatedUnion('bankAccountModel', [IbanAccount, ClabeAccount, RbcAccount]);

const getModeById = id => (id ? Modes.EDIT : Modes.CREATE);

const fieldsToBackend = fields => ({
    name: fields.bankAccountName,
    description: fields.description,
    iban_details:
        fields.bankAccountModel === BankAccountModel.IBAN
            ? {
                  iban: fields.iban,
                  bic_number: fields.bic
              }
            : undefined,
    clabe_details:
        fields.bankAccountModel === BankAccountModel.CLABE
            ? {
                  clabe_number: fields.clabe,
                  bank_name: fields.bankName
              }
            : undefined,
    rbc_details:
        fields.bankAccountModel === BankAccountModel.RBC
            ? {
                  transit_number: fields.transitNumber,
                  institution_number: fields.institutionNumber,
                  account_number: fields.accountNumber
              }
            : undefined
});

const fieldsFromBackend = data => ({
    bankAccountName: data.name || '',
    description: data.description || '',
    bankAccountModel: data.account_type || '',
    iban: data.account_details?.iban || '',
    bic: data.account_details?.bic_number || '',
    clabe: data.account_details?.clabe_number || '',
    bankName: data.account_details?.bank_name || '',
    transitNumber: data.account_details?.transit_number || '',
    institutionNumber: data.account_details?.institution_number || '',
    accountNumber: data.account_details?.account_number || ''
});

const EMPTY_STATE = {
    bankAccountName: '',
    description: '',
    bankAccountModel: null,
    iban: '',
    bic: '',
    clabe: '',
    bankName: '',
    transitNumber: '',
    institutionNumber: '',
    accountNumber: ''
};

export const EditBankAccountDrawerProvider = ({ children }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const mode = getModeById(editId);
    const store = useBankAccountsStore();
    const toaster = useContext(ToastContext);

    const form = useForm({
        resolver: zodResolver(FormModel)
    });

    const { reset } = form;

    const closeModal = () => setIsOpen(false);

    const handleErrors = error => {
        const detail = error.response?.detail;

        if (Array.isArray(detail)) {
            detail.forEach(it => {
                const path = it.loc.join('.');

                if (path === 'body.iban_details.iban') {
                    form.setError('iban', {
                        type: 'server',
                        message: it.msg
                    });
                } else if (path === 'body.clabe_details.clabe_number') {
                    form.setError('clabe', {
                        type: 'server',
                        message: it.msg
                    });
                } else if (path === 'body.rbc_details.transit_number') {
                    form.setError('transitNumber', {
                        type: 'server',
                        message: it.msg
                    });
                } else if (path === 'body.rbc_details.institution_number') {
                    form.setError('institutionNumber', {
                        type: 'server',
                        message: it.msg
                    });
                } else if (path === 'body.rbc_details.account_number') {
                    form.setError('accountNumber', {
                        type: 'server',
                        message: it.msg
                    });
                } else {
                    toaster.error(it.msg);
                }
            });
        }
    };

    const createMutation = useMutation(data => store.createBankAccount(data), {
        onSuccess: () => {
            closeModal();
            toaster.success(t('sauron.financeModule.bankAccounts.drawer.create.successToast'));
        },
        onError: handleErrors
    });

    const updateMutation = useMutation(data => store.updateBankAccount(editId, data), {
        onSuccess: () => {
            closeModal();
            toaster.success(t('sauron.financeModule.bankAccounts.drawer.edit.successToast'));
        },
        onError: handleErrors
    });

    const handleSubmit = formData => {
        switch (mode) {
            case Modes.CREATE:
                createMutation.mutate(fieldsToBackend(formData));
                break;
            case Modes.EDIT:
                updateMutation.mutate(fieldsToBackend(formData));
                break;
            default:
                break;
        }
    };

    const value = useMemo(() => {
        const edit = document => {
            setIsOpen(true);
            setEditId(document.id);
            reset(fieldsFromBackend(document));
        };

        const create = () => {
            setIsOpen(true);
            setEditId(null);
            reset(EMPTY_STATE);
        };

        return {
            edit,
            create
        };
    }, [reset]);

    return (
        <EditBankAccountDrawerContext.Provider value={value}>
            <FormProvider {...form}>
                <BankAccountDrawer
                    isOpen={isOpen}
                    mode={mode}
                    onClose={closeModal}
                    onSubmit={form.handleSubmit(handleSubmit)}
                    isSubmitting={updateMutation.isLoading || createMutation.isLoading}
                />
            </FormProvider>
            {children}
        </EditBankAccountDrawerContext.Provider>
    );
};

EditBankAccountDrawerProvider.propTypes = {
    children: PropTypes.node
};
